/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplateTargetPicker.tsx
 * FILE_ID: QA-TEMPLATES-TARGET-001
 * PURPOSE: Tick, rename, and add insert-target areas for desktop and phone.
 * LANDMINE: Rename writes through renameArea. Never cache the new name locally.
 */
import { useState } from 'react'
import { ChevronDown, Pencil } from 'lucide-react'
import { InlineRenameInput } from '../../../qa/InlineRenameInput'
import type { QaTemplatePicker } from './useQaTemplatePicker'

export function QaTemplateTargetPicker({
  picker,
  uncapped = false,
  onCollapse,
}: {
  picker: QaTemplatePicker
  /** Phone full sheet scrolls the list. Desktop keeps the 45vh cap. */
  uncapped?: boolean
  /** Desktop popup passes it. Phone uses Back. */
  onCollapse?: () => void
}) {
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  return (
    <div data-testid="qa-templates-target-picker" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Insert into</p>
        {onCollapse ? (
          <button
            type="button"
            aria-label="Collapse locations"
            title="Collapse"
            onClick={onCollapse}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className={uncapped ? 'space-y-1' : 'max-h-[45vh] space-y-1 overflow-y-auto'}>
        {picker.pendingAreaNames.map((name, index) => {
          const pendingKey = `pending-${index}`
          return (
            <div key={pendingKey} className="flex min-h-[44px] min-w-0 items-center gap-2 px-1">
              {renamingId === pendingKey ? (
                <InlineRenameInput
                  initialValue={name}
                  busy={picker.busy}
                  ariaLabel="Rename location"
                  onSubmit={(next) => {
                    picker.renamePendingArea(index, next)
                    setRenamingId(null)
                  }}
                  onCancel={() => setRenamingId(null)}
                  className="flex-1"
                />
              ) : (
                <>
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => {}}
                      className="h-4 w-4 rounded border-border"
                    />
                    <span className="min-w-0 truncate text-sm">{name}</span>
                  </label>
                  <button
                    type="button"
                    aria-label="Rename location"
                    onClick={() => setRenamingId(pendingKey)}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          )
        })}
        {picker.areas.map((area) => {
          const checked = picker.targetAreaIds.has(area.id)
          return (
            <div key={area.id} className="flex min-h-[44px] min-w-0 items-center gap-2 px-1">
              {renamingId === area.id ? (
                <InlineRenameInput
                  initialValue={area.area_name}
                  busy={picker.busy}
                  ariaLabel="Rename location"
                  onSubmit={(name) => {
                    void picker.renameTargetArea(area.id, name)
                    setRenamingId(null)
                  }}
                  onCancel={() => setRenamingId(null)}
                  className="flex-1"
                />
              ) : (
                <>
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => picker.toggleTarget(area.id)}
                      className="h-4 w-4 rounded border-border"
                    />
                    <span className="min-w-0 truncate text-sm">{area.area_name}</span>
                  </label>
                  <button
                    type="button"
                    aria-label="Rename location"
                    onClick={() => setRenamingId(area.id)}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          )
        })}
      </div>
      {adding ? (
        <InlineRenameInput
          initialValue=""
          ariaLabel="New location name"
          busy={picker.busy}
          onSubmit={(name) => {
            if (!name.trim()) return
            void picker.addTargetArea(name)
            setAdding(false)
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex min-h-[44px] w-full items-center px-1 text-left text-sm font-medium"
        >
          + Add Location
        </button>
      )}
      <p className="text-xs text-muted-foreground">
        With no location ticked, each checklist goes into its own location (Roof, Floors, Walls and so on), created when missing. Tick locations to insert the checklists only into those locations.
      </p>
      {picker.error ? (
        <p role="alert" className="text-xs text-destructive">{picker.error}</p>
      ) : null}
    </div>
  )
}
